// src/components/Admin/EventosAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
    Box, Paper, Typography, Button, IconButton, TextField, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Stack, Snackbar,
    Alert, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
    TablePagination, Tooltip, Chip, Divider, MenuItem, Select, FormControl, InputLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GetAppIcon from "@mui/icons-material/GetApp";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

dayjs.locale("es");

const API = process.env.REACT_APP_API_URL;

const headersAuth = () => {
    const token = localStorage.getItem("token");
    return { Authorization: token ? `Bearer ${token}` : undefined };
};

// Parse/format helpers
const parseDate = (v) => (v ? dayjs(v) : null);
const fmt = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : ""); // -> ISO (para backend)
const fmtES = (iso) => {                                      // -> DD/MM/YYYY (para UI/Excel)
    if (!iso) return "-";
    const d = dayjs(iso);
    return d.isValid() ? d.format("DD/MM/YYYY") : iso;
};

const ESTADOS = ["ABIERTO", "CERRADO"];

const EMPTY_EVENTO = {
    id: null,
    nombre: "",
    descripcion: "",
    estado: "ABIERTO",
    fecha: null,     // informativa (mínima de 'fechas')
    tope: "",
    fechas: [],      // array de "YYYY-MM-DD"
};

const nonEmpty = (s) => typeof s === "string" && s.trim() !== "";

export default function EventosAdmin() {
    const [tab, setTab] = useState(0);

    // -------- EVENTOS (CRUD) --------
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [sort, setSort] = useState("fecha,asc");
    const [loading, setLoading] = useState(false);

    const [q, setQ] = useState("");
    const [estado, setEstado] = useState("");
    const [fecha_from, setFechaFrom] = useState(null);
    const [fecha_to, setFechaTo] = useState(null);

    const [openEdit, setOpenEdit] = useState(false);
    const [editing, setEditing] = useState(EMPTY_EVENTO);
    const [fechasTmp, setFechasTmp] = useState([]); // dayjs[]

    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

    // -------- INSCRITOS --------
    const [ins_rows, setInsRows] = useState([]);
    const [ins_exporting, setInsExporting] = useState(false);
    const [ins_total, setInsTotal] = useState(0);
    const [ins_page, setInsPage] = useState(0);
    const [ins_size, setInsSize] = useState(25);
    const [ins_sort, setInsSort] = useState("dia,asc");
    const [ins_loading, setInsLoading] = useState(false);

    const [evento_id, setEventoId] = useState("");
    const [codigo, setCodigo] = useState("");
    const [q_nombre, setQNombre] = useState("");
    const [dia_from, setDiaFrom] = useState(null);
    const [dia_to, setDiaTo] = useState(null);
    const [familia, setFamilia] = useState("");
    const [entradas_min, setEntradasMin] = useState("");
    const [entradas_max, setEntradasMax] = useState("");

    // cache eventos (para combos en inscritos)
    const [eventos_lite, setEventosLite] = useState([]);

    const api = useMemo(() => axios.create({ baseURL: API, withCredentials: false }), []);
    const handleSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

    // cache colegiados por código
    const colegiadoCache = useRef(new Map()); // codigo -> {nombre, apellido_1, apellido_2, email}

    useEffect(() => { fetchEventos(); }, [page, size, sort, q, estado, fecha_from, fecha_to]);
    useEffect(() => { fetchInscritos(); }, [ins_page, ins_size, ins_sort, evento_id, codigo, q_nombre, dia_from, dia_to, familia, entradas_min, entradas_max]);
    useEffect(() => { preloadEventosLite(); }, []);

    // ================== EVENTOS ==================
    async function fetchEventos() {
        try {
            setLoading(true);
            const params = {
                page, size, sort,
                q: q || undefined,
                estado: estado || undefined,
                fecha_from: fecha_from ? fmt(fecha_from) : undefined,
                fecha_to: fecha_to ? fmt(fecha_to) : undefined,
            };
            const { data } = await api.get("/api/admin/eventos/page", { params, headers: headersAuth() });
            setRows(data.content || []);
            setTotal(data.totalElements ?? data.total_elements ?? data.total ?? 0);
        } catch {
            handleSnack("Error al cargar eventos", "error");
        } finally {
            setLoading(false);
        }
    }

    async function preloadEventosLite() {
        try {
            const { data } = await api.get("/api/admin/eventos/page", {
                params: { page: 0, size: 1000, sort: "fecha,asc" },
                headers: headersAuth(),
            });
            const list = (data.content || []).map(e => ({
                id: e.id,
                nombre: e.nombre,
                estado: e.estado,
                tope: e.tope ?? null,
            }));
            setEventosLite(list);
        } catch { /* ignore */ }
    }

    const resetForm = () => {
        setEditing(EMPTY_EVENTO);
        setFechasTmp([]);
    };

    const openNew = () => { resetForm(); setOpenEdit(true); };

    const openEditDialog = (row) => {
        const fechasArr = Array.isArray(row?.fechas) ? row.fechas : [];
        setEditing({
            ...EMPTY_EVENTO,
            ...row,
            fecha: parseDate(row?.fecha),
            tope: row?.tope ?? "",
            fechas: fechasArr,
        });
        setFechasTmp(fechasArr.map(parseDate));
        setOpenEdit(true);
    };

    async function saveEvento() {
        try {
            const fechasOrdenadas = Array.isArray(editing?.fechas) ? editing.fechas.slice().sort() : [];
            const payload = {
                nombre: editing?.nombre || "",
                descripcion: editing?.descripcion || "",
                estado: editing?.estado || "",
                tope: editing?.tope !== "" && editing?.tope !== null ? parseInt(editing.tope, 10) : null,
                fechas: fechasOrdenadas,
                fecha: fechasOrdenadas[0] || null, // informativo; backend recalcula a mínima igualmente
            };
            if (editing?.id) {
                await api.put(`/api/admin/eventos/${editing.id}`, payload, { headers: headersAuth() });
                handleSnack("Evento actualizado");
            } else {
                await api.post(`/api/admin/eventos`, payload, { headers: headersAuth() });
                handleSnack("Evento creado");
            }
            setOpenEdit(false);
            fetchEventos();
            preloadEventosLite();
        } catch {
            handleSnack("Error al guardar el evento", "error");
        }
    }

    async function deleteEvento(id) {
        if (!window.confirm("¿Eliminar evento? Esta acción no se puede deshacer.")) return;
        try {
            await api.delete(`/api/admin/eventos/${id}`, { headers: headersAuth() });
            handleSnack("Evento eliminado");
            fetchEventos();
            preloadEventosLite();
        } catch {
            handleSnack("No se pudo eliminar", "error");
        }
    }

    async function cerrarEvento(id) {
        try {
            await api.put(`/api/admin/eventos/${id}`, { estado: "CERRADO" }, { headers: headersAuth() });
            handleSnack("Evento cerrado");
            fetchEventos();
            preloadEventosLite();
        } catch {
            handleSnack("No se pudo cerrar el evento", "error");
        }
    }

    async function reabrirEvento(id) {
        try {
            await api.put(`/api/admin/eventos/${id}`, { estado: "ABIERTO" }, { headers: headersAuth() });
            handleSnack("Evento reabierto");
            fetchEventos();
            preloadEventosLite();
        } catch {
            handleSnack("No se pudo reabrir el evento", "error");
        }
    }

    // ================== INSCRITOS ==================
    // Obtiene un colegiado por código (snake_case esperado) con cache
    const fetchColegiado = async (codigoColegiado) => {
        if (!codigoColegiado) return null;
        if (colegiadoCache.current.has(codigoColegiado)) {
            return colegiadoCache.current.get(codigoColegiado);
        }
        try {
            const { data } = await api.get(`/api/admin/colegiados/${codigoColegiado}`, { headers: headersAuth() });
            const c = {
                nombre: data?.nombre ?? "",
                apellido_1: data?.apellido_1 ?? "",
                apellido_2: data?.apellido_2 ?? "",
                email: data?.email ?? "",
            };
            colegiadoCache.current.set(codigoColegiado, c);
            return c;
        } catch {
            colegiadoCache.current.set(codigoColegiado, null);
            return null;
        }
    };

    // Limitador de concurrencia simple
    async function withConcurrency(items, limit, worker) {
        const ret = [];
        let i = 0;
        const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
            while (i < items.length) {
                const idx = i++;
                ret[idx] = await worker(items[idx], idx);
            }
        });
        await Promise.all(runners);
        return ret;
    }

    async function enrichWithColegiados(rows) {
        const needs = rows.map((r, idx) => ({
            idx,
            codigo: r.codigo_colegiado,
            needAp: !(nonEmpty(r.apellido_1) || nonEmpty(r.apellido_2)),
            needNm: !nonEmpty(r.nombre),
            needEm: !nonEmpty(r.email),
        })).filter(x => x.needAp || x.needNm || x.needEm);

        if (needs.length === 0) return rows;

        await withConcurrency(needs, 5, async ({ idx, codigo }) => {
            const info = await fetchColegiado(codigo);
            if (info) {
                rows[idx] = {
                    ...rows[idx],
                    nombre: rows[idx].nombre || info.nombre || "",
                    apellido_1: rows[idx].apellido_1 || info.apellido_1 || "",
                    apellido_2: rows[idx].apellido_2 || info.apellido_2 || "",
                    email: rows[idx].email || info.email || "",
                };
            }
        });

        return rows;
    }

    async function fetchInscritos() {
        try {
            setInsLoading(true);
            const params = {
                page: ins_page, size: ins_size, sort: ins_sort,
                evento_id: evento_id || undefined,
                codigo: codigo || undefined,
                q_nombre: q_nombre || undefined,
                dia_from: dia_from ? fmt(dia_from) : undefined,
                dia_to: dia_to ? fmt(dia_to) : undefined,
                familia: familia || undefined,
                entradas_min: entradas_min || undefined,
                entradas_max: entradas_max || undefined,
            };
            const { data } = await api.get("/api/admin/eventos/inscritos/page", { params, headers: headersAuth() });

            const localTotal = data.totalElements ?? data.total_elements ?? data.total ?? 0;
            let localRows = data.content || [];

            // Enriquecer con datos de 'colegiados' si faltan (snake_case)
            localRows = await enrichWithColegiados(localRows);

            setInsRows(localRows);
            setInsTotal(localTotal);

            if (evento_id) {
                const ev = eventos_lite.find(e => e.id === Number(evento_id));
                if (ev?.tope != null && ev?.estado !== "CERRADO" && localTotal >= Number(ev.tope)) {
                    handleSnack(`Tope alcanzado en "${ev.nombre}" (${ev.tope}).`, "warning");
                }
            }
        } catch {
            handleSnack("Error al cargar inscritos", "error");
        } finally {
            setInsLoading(false);
        }
    }

    async function fetchAllInscritosForExport() {
        // OJO: usa los mismos filtros actuales (evento_id, codigo, q_nombre, dia_from, dia_to, familia, entradas_min, entradas_max)
        const baseParams = {
            sort: ins_sort,
            evento_id: evento_id || undefined,
            codigo: codigo || undefined,
            q_nombre: q_nombre || undefined,
            dia_from: dia_from ? fmt(dia_from) : undefined,
            dia_to: dia_to ? fmt(dia_to) : undefined,
            familia: familia || undefined,
            entradas_min: entradas_min || undefined,
            entradas_max: entradas_max || undefined,
        };

        // Tamaño grande para minimizar llamadas (ajústalo si tu backend limita size)
        const EXPORT_SIZE = 6000;

        let pageIdx = 0;
        let all = [];
        let totalExpected = null;

        // Salvaguarda por si algo raro ocurre (evita bucles infinitos)
        const MAX_PAGES = 5000;

        while (pageIdx < MAX_PAGES) {
            const params = { ...baseParams, page: pageIdx, size: EXPORT_SIZE };

            const { data } = await api.get("/api/admin/eventos/inscritos/page", {
                params,
                headers: headersAuth(),
            });

            const content = data?.content || [];
            const total = data?.totalElements ?? data?.total_elements ?? data?.total ?? null;

            if (totalExpected == null && total != null) totalExpected = Number(total);

            if (!content.length) break;

            // Enriquecer con datos de colegiados si faltan
            const enriched = await enrichWithColegiados(content);

            all = all.concat(enriched);

            // Si el backend nos dijo el total, cortamos cuando lo alcancemos
            if (totalExpected != null && all.length >= totalExpected) break;

            pageIdx += 1;
        }

        // Por si el total venía “inflado” o hubo duplicados, recorta al total
        if (totalExpected != null && all.length > totalExpected) {
            all = all.slice(0, totalExpected);
        }

        return all;
    }

    async function exportInscritosExcel() {
        if (ins_exporting) return;

        try {
            setInsExporting(true);
            handleSnack("Generando exportación completa…", "info");

            const allRows = await fetchAllInscritosForExport();

            const data = allRows.map(r => ({
                id_evento: r.id_evento,
                codigo_colegiado: r.codigo_colegiado,
                nombre: r.nombre || "",
                apellido_1: r.apellido_1 || "",
                apellido_2: r.apellido_2 || "",
                email: r.email || "",
                dia: fmtES(r.dia),
                familia: r.familia || "",
                entradas: r.entradas ?? "",
                otro: r.otro || "",
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "inscritos");

            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const nombre = `inscritos_evento_${evento_id || "todos"}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`;
            saveAs(new Blob([wbout], { type: "application/octet-stream" }), nombre);

            handleSnack(`Exportación generada (${data.length} filas)`);
        } catch (e) {
            console.error(e);
            handleSnack("Error al exportar todos los inscritos", "error");
        } finally {
            setInsExporting(false);
        }
    }

    async function closeIfTopeReached() {
        if (!evento_id) return;
        const ev = eventos_lite.find(e => e.id === Number(evento_id));
        if (!ev) return;
        if (ev.estado === "CERRADO") {
            handleSnack("El evento ya está cerrado", "info");
            return;
        }
        if (ev.tope == null) {
            handleSnack("El evento no tiene tope definido", "info");
            return;
        }
        if (ins_total >= ev.tope) {
            await cerrarEvento(ev.id);
        } else {
            handleSnack(`Aún no se alcanzó el tope (${ins_total}/${ev.tope})`, "info");
        }
    }

    const estadoChip = (estado) => {
        const color =
            estado === "CERRADO" ? "error" :
                estado === "ABIERTO" ? "success" : "default";
        return <Chip size="small" label={estado || "-"} color={color} variant="outlined" />;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Box sx={{ p: 2 }}>
                <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Eventos (ADMIN)</Typography>
                        <Button variant={tab === 0 ? "contained" : "text"} onClick={() => setTab(0)}>Gestión</Button>
                        <Button variant={tab === 1 ? "contained" : "text"} onClick={() => setTab(1)}>Inscritos</Button>
                    </Stack>

                    {tab === 0 && (
                        <>
                            {/* Filtros eventos */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={3}>
                                    <TextField label="Buscar nombre" value={q} onChange={e => setQ(e.target.value)} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth>
                                        <InputLabel>Estado</InputLabel>
                                        <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
                                            <MenuItem value=""><em>Todos</em></MenuItem>
                                            {ESTADOS.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <DatePicker
                                        label="Fecha desde"
                                        value={fecha_from}
                                        onChange={setFechaFrom}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <DatePicker
                                        label="Fecha hasta"
                                        value={fecha_to}
                                        onChange={setFechaTo}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Tooltip title="Refrescar"><IconButton onClick={fetchEventos}><RefreshIcon /></IconButton></Tooltip>
                                        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>Nuevo</Button>
                                    </Stack>
                                </Grid>
                            </Grid>

                            {/* Tabla eventos */}
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell>Fecha (mín.)</TableCell>
                                            <TableCell>Fechas</TableCell>
                                            <TableCell>Estado</TableCell>
                                            <TableCell>Tope</TableCell>
                                            <TableCell align="right">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row) => (
                                            <TableRow key={row.id} hover>
                                                <TableCell>{row.id}</TableCell>
                                                <TableCell>{row.nombre}</TableCell>
                                                <TableCell>{fmtES(row.fecha)}</TableCell>
                                                <TableCell>
                                                    {(row.fechas?.length ?? 0) > 0
                                                        ? (
                                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                                {(row.fechas || []).slice(0, 3).map((f) => (
                                                                    <Chip key={f} size="small" label={fmtES(f)} />
                                                                ))}
                                                                {(row.fechas?.length || 0) > 3 && (
                                                                    <Chip size="small" variant="outlined" label={`+${row.fechas.length - 3}`} />
                                                                )}
                                                            </Stack>
                                                        )
                                                        : "-"
                                                    }
                                                </TableCell>
                                                <TableCell>{estadoChip(row.estado)}</TableCell>
                                                <TableCell>{row.tope ?? "-"}</TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        {row.estado !== "CERRADO" ? (
                                                            <Tooltip title="Cerrar evento">
                                                                <IconButton onClick={() => cerrarEvento(row.id)}><LockIcon /></IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Reabrir evento">
                                                                <IconButton onClick={() => reabrirEvento(row.id)}><LockOpenIcon /></IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Editar">
                                                            <IconButton onClick={() => openEditDialog(row)}><EditIcon /></IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Eliminar">
                                                            <IconButton onClick={() => deleteEvento(row.id)}><DeleteIcon /></IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {rows.length === 0 && !loading && (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">Sin resultados</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(e, p) => setPage(p)}
                                rowsPerPage={size}
                                onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                                labelRowsPerPage="Filas por página"
                            />
                        </>
                    )}

                    {tab === 1 && (
                        <>
                            {/* Filtros inscritos */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Evento</InputLabel>
                                        <Select
                                            label="Evento"
                                            value={evento_id}
                                            onChange={(e) => { setEventoId(e.target.value); setInsPage(0); }}
                                        >
                                            <MenuItem value=""><em>Todos</em></MenuItem>
                                            {eventos_lite.map(ev => (
                                                <MenuItem key={ev.id} value={ev.id}>
                                                    {ev.nombre} {ev.tope != null ? ` (tope: ${ev.tope})` : ""} {ev.estado === "CERRADO" ? " [CERRADO]" : ""}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField label="Código" value={codigo} onChange={e => setCodigo(e.target.value)} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField label="Nombre contiene" value={q_nombre} onChange={e => setQNombre(e.target.value)} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <DatePicker
                                        label="Día desde"
                                        value={dia_from}
                                        onChange={setDiaFrom}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <DatePicker
                                        label="Día hasta"
                                        value={dia_to}
                                        onChange={setDiaTo}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField label="Familia" value={familia} onChange={e => setFamilia(e.target.value)} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField label="Entradas mín." type="number" value={entradas_min} onChange={e => setEntradasMin(e.target.value)} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField label="Entradas máx." type="number" value={entradas_max} onChange={e => setEntradasMax(e.target.value)} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Tooltip title="Refrescar"><IconButton onClick={fetchInscritos}><RefreshIcon /></IconButton></Tooltip>
                                        <Tooltip title={ins_exporting ? "Exportando…" : "Exportar Excel"}>
                                            <span>
                                                <IconButton onClick={exportInscritosExcel} disabled={ins_exporting}>
                                                    <GetAppIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Cerrar por tope (si procede)">
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    startIcon={<DoneAllIcon />}
                                                    disabled={!evento_id}
                                                    onClick={closeIfTopeReached}
                                                >
                                                    Cerrar si tope alcanzado
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                            </Grid>

                            {/* Tabla inscritos */}
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID evento</TableCell>
                                            <TableCell>Código</TableCell>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell>Apellidos</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Día</TableCell>
                                            <TableCell>Familia</TableCell>
                                            <TableCell>Entradas</TableCell>
                                            <TableCell>Otro</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ins_rows.map((r, idx) => (
                                            <TableRow key={`${r.id_evento}-${r.codigo_colegiado}-${idx}`} hover>
                                                <TableCell>{r.id_evento}</TableCell>
                                                <TableCell>{r.codigo_colegiado}</TableCell>
                                                <TableCell>{r.nombre || "-"}</TableCell>
                                                <TableCell>{[r.apellido_1, r.apellido_2].filter(Boolean).join(" ") || "-"}</TableCell>
                                                <TableCell>{r.email || "-"}</TableCell>
                                                <TableCell>{fmtES(r.dia)}</TableCell>
                                                <TableCell>{r.familia || "-"}</TableCell>
                                                <TableCell>{r.entradas ?? "-"}</TableCell>
                                                <TableCell>{r.otro || "-"}</TableCell>
                                            </TableRow>
                                        ))}
                                        {ins_rows.length === 0 && !ins_loading && (
                                            <TableRow><TableCell colSpan={9} align="center">Sin resultados</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={ins_total}
                                page={ins_page}
                                onPageChange={(e, p) => setInsPage(p)}
                                rowsPerPage={ins_size}
                                onRowsPerPageChange={(e) => { setInsSize(parseInt(e.target.value, 10)); setInsPage(0); }}
                                labelRowsPerPage="Filas por página"
                            />
                        </>
                    )}
                </Paper>
            </Box>

            {/* Diálogo crear/editar evento */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editing?.id ? "Editar evento" : "Nuevo evento"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre"
                        value={editing?.nombre || ""}
                        onChange={(e) => setEditing(prev => ({ ...prev, nombre: e.target.value }))}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Descripción"
                        value={editing?.descripcion || ""}
                        onChange={(e) => setEditing(prev => ({ ...prev, descripcion: e.target.value }))}
                        fullWidth
                        margin="normal"
                        multiline
                        minRows={3}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    label="Estado"
                                    value={editing?.estado || ""}
                                    onChange={(e) => setEditing(prev => ({ ...prev, estado: e.target.value }))}
                                >
                                    {ESTADOS.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Tope de inscritos"
                                type="number"
                                value={editing?.tope ?? ""}
                                onChange={(e) => setEditing(prev => ({ ...prev, tope: e.target.value }))}
                                fullWidth
                                margin="normal"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Fecha (mínima, solo informativa)"
                                value={fmtES(((editing?.fechas || []).slice().sort()[0]) || "")}
                                fullWidth
                                margin="normal"
                                InputProps={{ readOnly: true }}
                                helperText="Se calcula automáticamente como la mínima de 'Fechas del evento'"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Fechas del evento (múltiples) */}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Fechas del evento</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label="Añadir fecha"
                                value={fechasTmp.length ? fechasTmp[fechasTmp.length - 1] : null}
                                onChange={(v) => {
                                    if (!v) return;
                                    const val = fmt(v); // guardamos en ISO
                                    setEditing(prev => {
                                        const ya = (prev?.fechas || []);
                                        if (ya.includes(val)) return prev;
                                        const nuevas = [...ya, val].sort();
                                        return { ...prev, fechas: nuevas };
                                    });
                                    setFechasTmp(prev => ([...(prev || []), v]));
                                }}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {(editing?.fechas || []).sort().map((f) => (
                                    <Chip
                                        key={f}
                                        label={fmtES(f)}
                                        onDelete={() => {
                                            setEditing(prev => ({ ...prev, fechas: (prev?.fechas || []).filter(x => x !== f) }));
                                            setFechasTmp(prev => (prev || []).filter(d => fmt(d) !== f));
                                        }}
                                        variant="outlined"
                                    />
                                ))}
                                {(!editing?.fechas || editing.fechas.length === 0) && (
                                    <Typography variant="body2" color="text.secondary">Añade al menos una fecha</Typography>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                        El backend valida que la fecha escogida por el colegiado esté en este listado y cierra el evento automáticamente al alcanzar el <b>tope</b>.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={saveEvento}>
                        {editing?.id ? "Guardar cambios" : "Crear"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </LocalizationProvider>
    );
}
